/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Search, Filter, FilterX } from 'lucide-react';
import { IssueType, IssueStatus, EstimateSize } from '@shared/utils/issues';

interface IssueFiltersProps {
  searchTerm: string;
  statusFilter: string;
  typeFilter: string;
  estimateFilter: string;
  excludeOnHold: boolean;
  onSearchChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onTypeFilterChange: (value: string) => void;
  onEstimateFilterChange: (value: string) => void;
  onExcludeOnHoldChange: (value: boolean) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  issueTypes: IssueType[];
  statusOptions: IssueStatus[];
  estimateOptions: EstimateSize[];
}

export function IssueFilters({
  searchTerm,
  statusFilter,
  typeFilter,
  estimateFilter,
  excludeOnHold,
  onSearchChange,
  onStatusFilterChange,
  onTypeFilterChange,
  onEstimateFilterChange,
  onExcludeOnHoldChange,
  onClearFilters,
  hasActiveFilters,
  issueTypes,
  statusOptions,
  estimateOptions
}: IssueFiltersProps) {
  const handleShowOnHold = () => {
    if (statusFilter === 'On Hold') {
      onStatusFilterChange('all');
    } else {
      onStatusFilterChange('On Hold');
      onExcludeOnHoldChange(false);
    }
  };
  const handleHideOnHold = () => {
    const next = !excludeOnHold;
    onExcludeOnHoldChange(next);
    if (next && statusFilter === 'On Hold') {
      onStatusFilterChange('all');
    }
  };
  return (
    <div style={{
      border: '1px solid #ddd',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
      background: '#f8f9fa'
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* Search Input */}
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#6c757d',
            pointerEvents: 'none'
          }} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by title, description, labels, or assignee..."
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Quick filters: On Hold (show only) and Hide On Hold */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500' }}>Quick filter:</span>
          <button
            type="button"
            onClick={handleShowOnHold}
            style={{
              padding: '6px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              backgroundColor: statusFilter === 'On Hold' ? '#ffc107' : 'white',
              color: statusFilter === 'On Hold' ? '#1a1a1a' : '#374151'
            }}
            title="Show only issues that are on hold (paused or blocked work)"
          >
            On Hold
          </button>
          <button
            type="button"
            onClick={handleHideOnHold}
            style={{
              padding: '6px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              backgroundColor: excludeOnHold ? '#e5e7eb' : 'white',
              color: excludeOnHold ? '#1f2937' : '#374151'
            }}
            title="Hide issues that are on hold"
          >
            Hide On Hold
          </button>
        </div>

        {/* Filter Dropdowns */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Filter size={14} style={{ color: '#6c757d' }} />
            <span style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500' }}>Filters:</span>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Statuses</option>
            {statusOptions.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => onTypeFilterChange(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Types</option>
            {issueTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          {/* Estimate Filter */}
          <select
            value={estimateFilter}
            onChange={(e) => onEstimateFilterChange(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Estimates</option>
            {estimateOptions.map(estimate => (
              <option key={estimate} value={estimate}>{estimate}</option>
            ))}
          </select>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="btn btn-secondary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                fontSize: '14px',
                marginLeft: 'auto'
              }}
              title="Clear all filters and search"
            >
              <FilterX size={14} />
              Clear Filters
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

